// frontend/src/pages/org/org-team-member-page.tsx
// Read DESIGN.md before modifying this file.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { Link, Navigate, useParams } from 'react-router';
import { ArrowLeft, ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RoleGate } from '@/components/auth/role-gate';
import { orgUrl } from '@/lib/org-url';
import { EmailInput } from '@/components/redacted-email';
import { maskEmail } from '@/lib/mask-email';
import {
  useAssignTeamMemberWorkspace,
  useOrgTeam,
  useOrgTeamWorkspaceOptions,
  useUnassignTeamMemberWorkspace,
  useUpdateTeamMemberProfile,
  useUpdateTeamMemberRole,
  type OrgTeamWorkspaceOption,
  type TeamMemberRow,
} from './org-team-queries';
import { PageContainer } from '@/components/page-container';

/**
 * Workspace badges inside the dropdown trigger: shows as many as actually
 * fit the trigger's width and collapses the rest into a +N badge (operator
 * ask: "2 or 4 badges, depends on the name length"). Fit is measured, not
 * estimated: a hidden duplicate row renders every badge plus a worst-case
 * "+99" sample, and a ResizeObserver recomputes on container resize.
 * Always shows at least one badge.
 */
function WorkspaceBadges({ workspaces }: { workspaces: { publicId: string; name: string }[] }) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [visibleCount, setVisibleCount] = useState(workspaces.length);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measurer = measureRef.current;
    if (!container || !measurer) return;

    const GAP = 4; // gap-1
    const recompute = () => {
      const children = Array.from(measurer.children) as HTMLElement[];
      if (children.length < 2) return;
      const plusWidth = children[children.length - 1].offsetWidth;
      const available = container.clientWidth;

      let used = 0;
      let fits = 0;
      for (let i = 0; i < workspaces.length; i += 1) {
        used += children[i].offsetWidth + (i > 0 ? GAP : 0);
        if (used > available) break;
        fits += 1;
      }
      if (fits >= workspaces.length) {
        setVisibleCount(workspaces.length);
        return;
      }
      // Not everything fits: walk back until the visible set + the +N badge fit.
      used = 0;
      for (let i = 0; i < fits; i += 1) used += children[i].offsetWidth + (i > 0 ? GAP : 0);
      let count = fits;
      while (count > 1 && used + GAP + plusWidth > available) {
        count -= 1;
        used -= children[count].offsetWidth + GAP;
      }
      setVisibleCount(Math.max(1, count));
    };

    recompute();
    const observer = new ResizeObserver(recompute);
    observer.observe(container);
    return () => observer.disconnect();
  }, [workspaces]);

  const badge = (w: { publicId: string; name: string }) => (
    <Badge key={w.publicId} variant="outline" className="max-w-32 shrink-0 truncate">
      {w.name}
    </Badge>
  );

  return (
    <>
      <span
        ref={containerRef}
        className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden"
      >
        {workspaces.slice(0, visibleCount).map(badge)}
        {workspaces.length > visibleCount && (
          <Badge variant="outline" className="shrink-0">
            +{workspaces.length - visibleCount}
          </Badge>
        )}
      </span>
      <span
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute flex items-center gap-1"
      >
        {workspaces.map(badge)}
        <Badge variant="outline" className="shrink-0">
          +99
        </Badge>
      </span>
    </>
  );
}

function initials(m: TeamMemberRow): string {
  const parts = [m.firstName, m.lastName].filter(Boolean) as string[];
  if (parts.length > 0) return parts.map((p) => p.charAt(0).toUpperCase()).join('');
  return m.email.charAt(0).toUpperCase();
}

/**
 * Org Team member page (operator request 2026-06-12, modeled on GHL's team
 * member editor): /org/:orgId/team/:memberId.
 *
 * Sections:
 *  - User Info: first/last name + phone (PATCH .../profile, vendor-first
 *    name sync to WorkOS); email read-only (changing it is the owner-driven
 *    Phase 114 verification flow). Profile image + password are deliberately
 *    absent until upload storage / a WorkOS reset surface exist.
 *  - Roles & Permissions: user type (read-only), org role, and a
 *    multi-select Workspaces dropdown — check assigns, uncheck unassigns.
 *    Future home of module-level permissions.
 */
export function OrgTeamMemberPage() {
  return (
    <PageContainer variant="fill" className="gap-4">
      <RoleGate role="admin" fallback={<Navigate to="/" replace />}>
        <MemberContent />
      </RoleGate>
    </PageContainer>
  );
}

function MemberContent() {
  const { orgId, memberId } = useParams<{ orgId: string; memberId: string }>();
  const teamQuery = useOrgTeam(orgId);
  const workspacesQuery = useOrgTeamWorkspaceOptions(orgId, true);
  const updateProfile = useUpdateTeamMemberProfile(orgId);
  const updateRole = useUpdateTeamMemberRole(orgId);
  const assignWorkspace = useAssignTeamMemberWorkspace(orgId);
  const unassignWorkspace = useUnassignTeamMemberWorkspace(orgId);
  const [togglingWs, setTogglingWs] = useState<string | null>(null);
  const profileSnapshotRef = useRef<{ memberId: string; snapshot: string } | null>(null);
  const profileUserDirtyRef = useRef(false);

  const member = useMemo(
    () => teamQuery.data?.members.find((m) => m.publicId === memberId) ?? null,
    [memberId, teamQuery.data?.members],
  );
  const orgWorkspaces = useMemo(
    () => workspacesQuery.data?.workspaces ?? [],
    [workspacesQuery.data?.workspaces],
  );
  const loaded = !teamQuery.isPending && !workspacesQuery.isPending;

  const profileForm = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '',
    },
    onSubmit: async ({ value }) => {
      if (!member) return;
      try {
        await updateProfile.mutateAsync({
          memberId: member.publicId,
          firstName: value.firstName,
          lastName: value.lastName,
          phone: value.phone,
        });
        profileSnapshotRef.current = {
          memberId: member.publicId,
          snapshot: `${value.firstName}\u0000${value.lastName}\u0000${value.phone}`,
        };
        profileUserDirtyRef.current = false;
        toast.success('Profile updated');
      } catch (err) {
        toast.error(
          err && typeof err === 'object' && 'userMessage' in err
            ? ((err as { userMessage?: string }).userMessage ?? 'Failed to update profile')
            : 'Failed to update profile',
        );
      }
    },
  });

  useEffect(() => {
    if (!member) return;
    const snapshot = `${member.firstName ?? ''}\u0000${member.lastName ?? ''}\u0000${member.phone ?? ''}`;
    const previous = profileSnapshotRef.current;
    const sameMember = previous?.memberId === member.publicId;
    if (sameMember && previous.snapshot === snapshot) return;
    if (sameMember && profileUserDirtyRef.current) return;
    profileSnapshotRef.current = { memberId: member.publicId, snapshot };
    profileUserDirtyRef.current = false;
    profileForm.setFieldValue('firstName', member.firstName ?? '');
    profileForm.setFieldValue('lastName', member.lastName ?? '');
    profileForm.setFieldValue('phone', member.phone ?? '');
  }, [member, profileForm]);

  async function handleRoleChange(role: 'admin' | 'member') {
    if (!member || role === member.role) return;
    try {
      await updateRole.mutateAsync({ memberId: member.publicId, role });
      toast.success('Role updated');
    } catch (err) {
      toast.error(
        err && typeof err === 'object' && 'userMessage' in err
          ? ((err as { userMessage?: string }).userMessage ?? 'Failed to update role')
          : 'Failed to update role',
      );
    }
  }

  async function toggleWorkspace(ws: OrgTeamWorkspaceOption, assigned: boolean) {
    if (!member) return;
    setTogglingWs(ws.publicId);
    try {
      if (assigned) {
        await assignWorkspace.mutateAsync({
          workspacePublicId: ws.publicId,
          organizationMemberPublicId: member.publicId,
          // The workspace role mirrors the User Role field above: an org
          // admin joins assigned workspaces as admin, a member as member.
          role: member.role === 'admin' ? 'admin' : 'member',
        });
        toast.success('Workspace added');
      } else {
        const assignment = member.workspaces.find((w) => w.publicId === ws.publicId);
        if (!assignment) return;
        await unassignWorkspace.mutateAsync({
          workspacePublicId: ws.publicId,
          memberPublicId: assignment.memberPublicId,
        });
        toast.success('Workspace removed');
      }
    } catch (err) {
      toast.error(
        err && typeof err === 'object' && 'userMessage' in err
          ? ((err as { userMessage?: string }).userMessage ?? 'Failed to update workspaces')
          : 'Failed to update workspaces',
      );
    } finally {
      setTogglingWs(null);
    }
  }

  if (!loaded) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const loadError = teamQuery.error ?? workspacesQuery.error;
  if (loadError) {
    // Don't silently bounce on a transient API error — that hides the failure
    // and looks like the member vanished. Surface it with a retry instead.
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
        <p className="text-sm text-muted-foreground">
          {loadError.userMessage ?? 'Failed to load member. Please try again.'}
        </p>
        <Button
          variant="outline"
          onClick={() => {
            void teamQuery.refetch();
            void workspacesQuery.refetch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!member || !orgId) {
    // Stale or foreign member id: bounce back to the roster, same silent
    // convention as the other org pages.
    return <Navigate to={orgId ? orgUrl(orgId, 'team') : '/'} replace />;
  }

  const assignedIds = new Set(member.workspaces.map((w) => w.publicId));

  return (
    <>
      <div className="flex items-center gap-3">
        <Link
          to={orgUrl(orgId, 'team')}
          className={buttonVariants({ variant: 'ghost', size: 'icon-sm' })}
          aria-label="Back To Team"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <h1 className="truncate text-xl font-semibold">
          {member.displayName || maskEmail(member.email)}
        </h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>User Info</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex size-14 items-center justify-center rounded-full bg-primary text-lg font-semibold text-primary-foreground">
            {initials(member)}
          </div>
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void profileForm.handleSubmit();
            }}
            className="space-y-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <profileForm.Field name="firstName">
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor="member-first-name">First Name</Label>
                    <Input
                      id="member-first-name"
                      value={field.state.value}
                      onChange={(e) => {
                        profileUserDirtyRef.current = true;
                        field.handleChange(e.target.value);
                      }}
                      onBlur={field.handleBlur}
                    />
                  </div>
                )}
              </profileForm.Field>
              <profileForm.Field name="lastName">
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor="member-last-name">Last Name</Label>
                    <Input
                      id="member-last-name"
                      value={field.state.value}
                      onChange={(e) => {
                        profileUserDirtyRef.current = true;
                        field.handleChange(e.target.value);
                      }}
                      onBlur={field.handleBlur}
                    />
                  </div>
                )}
              </profileForm.Field>
            <div className="space-y-2">
              <Label htmlFor="member-email">Email</Label>
              <EmailInput id="member-email" value={member.email} disabled />
            </div>
              <profileForm.Field name="phone">
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor="member-phone">Phone</Label>
                    <Input
                      id="member-phone"
                      type="tel"
                      value={field.state.value}
                      onChange={(e) => {
                        profileUserDirtyRef.current = true;
                        field.handleChange(e.target.value);
                      }}
                      onBlur={field.handleBlur}
                      placeholder="+1 555 000 0000"
                    />
                  </div>
                )}
              </profileForm.Field>
            </div>
            <Button type="submit" disabled={updateProfile.isPending}>
              {updateProfile.isPending && <Loader2 className="size-3.5 animate-spin" />}
              Save Changes
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Roles &amp; Permissions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>User Type</Label>
              <div>
                {member.source === 'team' ? (
                  <Badge variant="secondary">Team</Badge>
                ) : (
                  <Badge variant="outline">Workspace</Badge>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="member-role">User Role</Label>
              <Select
                value={member.role as 'admin' | 'member'}
                onValueChange={(v) => {
                  if (v) void handleRoleChange(v as 'admin' | 'member');
                }}
              >
                <SelectTrigger id="member-role" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Workspaces</Label>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button variant="outline" className="w-full justify-between">
                    {member.workspaces.length === 0 ? (
                      'Select Workspaces'
                    ) : (
                      <WorkspaceBadges workspaces={member.workspaces} />
                    )}
                    <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
                  </Button>
                }
              />
              <DropdownMenuContent>
                {orgWorkspaces.map((w) => (
                  <DropdownMenuCheckboxItem
                    key={w.publicId}
                    checked={assignedIds.has(w.publicId)}
                    disabled={togglingWs !== null}
                    onCheckedChange={(checked) => void toggleWorkspace(w, checked === true)}
                    closeOnClick={false}
                  >
                    {w.name}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <p className="text-xs text-muted-foreground">
              Checking a workspace assigns this member to it. Module-level
              permissions will live here in a future release.
            </p>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
